import { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Shield, LogOut, Plus, Package, RefreshCw, Search, Truck, Vault, CheckCircle2, ScrollText,
  ExternalLink, Bell, MapPin, KeyRound, Building2, Save, Mail, Phone, Trash2, AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const SERVICE_TYPES = [
  "Diplomatic Pouch",
  "High-Security Vaulting",
  "Sovereign Air Courier",
  "Armored Ground",
  "International Logistics",
  "Deposit",
];

const STATUS_OPTIONS = [
  "Registered",
  "Dispatched",
  "In Diplomatic Transit",
  "Vaulted",
  "Customs & Diplomatic Clearance",
  "Out for Delivery",
  "Delivered",
];

function statusTone(status = "") {
  const s = status.toLowerCase();
  if (s.includes("deliver")) return "text-emerald-300 bg-emerald-500/10 border-emerald-500/40";
  if (s.includes("customs")) return "text-violet-300 bg-violet-500/10 border-violet-500/40";
  if (s.includes("transit") || s.includes("dispatch")) return "text-amber-300 bg-amber-500/10 border-amber-500/40";
  if (s.includes("vault")) return "text-emerald-300 bg-emerald-500/10 border-emerald-500/40";
  if (s.includes("registered")) return "text-sky-300 bg-sky-500/10 border-sky-500/40";
  return "text-slate-200 bg-slate-500/10 border-slate-500/40";
}

const initialCreate = {
  sender_name: "", sender_org: "", recipient_name: "", recipient_org: "",
  recipient_email: "", origin: "", destination: "", service_type: SERVICE_TYPES[0],
  weight_kg: "", declared_value: "", initial_status: "Registered",
  initial_location: "", estimated_delivery: "", notes: "", deposit_date: "", depositor_name: "",
};

const initialCheckpoint = { status: STATUS_OPTIONS[1], location: "", notes: "", notify_recipient: false, custom_status: "" };
const CUSTOM_STATUS_SENTINEL = "__custom__";

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [kpi, setKpi] = useState({ total: 0, in_transit: 0, vaulted: 0, delivered: 0, customs: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(initialCreate);
  const [creating, setCreating] = useState(false);

  const [cpOpen, setCpOpen] = useState(false);
  const [cpTarget, setCpTarget] = useState(null);
  const [cpForm, setCpForm] = useState(initialCheckpoint);
  const [cpSaving, setCpSaving] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPkg, setDetailPkg] = useState(null);

  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const { company, setCompany } = useCompanyInfo();
  const [companyForm, setCompanyForm] = useState(null);
  const [companySaving, setCompanySaving] = useState(false);
  const [companyDirty, setCompanyDirty] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    // Hydrate the editable form once the API-loaded company arrives.
    if (company && companyForm === null) {
      setCompanyForm({
        secure_email: company.secure_email,
        phone: company.phone,
        address: company.address,
      });
    }
  }, [company, companyForm]);

  const patchCompany = (patch) => {
    setCompanyForm((f) => ({ ...f, ...patch }));
    setCompanyDirty(true);
  };

  const submitCompany = async (e) => {
    e.preventDefault();
    if (!companyForm) return;
    setCompanySaving(true);
    try {
      const { data } = await api.put("/company", companyForm);
      setCompany(data);
      setCompanyForm({ secure_email: data.secure_email, phone: data.phone, address: data.address });
      setCompanyDirty(false);
      toast.success("Company details updated · public site now shows the new info");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setCompanySaving(false);
    }
  };

  // Deps intentionally empty: `api`, `toast`, `formatApiError` are stable
  // module-level references; setters are stable per React.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pkgs, k] = await Promise.all([api.get("/packages"), api.get("/packages/stats/kpi")]);
      setPackages(pkgs.data);
      setKpi(k.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return packages;
    return packages.filter(
      (p) =>
        p.tracking_id.toLowerCase().includes(t) ||
        (p.recipient_name || "").toLowerCase().includes(t) ||
        (p.origin || "").toLowerCase().includes(t) ||
        (p.destination || "").toLowerCase().includes(t)
    );
  }, [q, packages]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...createForm, weight_kg: parseFloat(createForm.weight_kg) || 0 };
      if (!payload.recipient_email) delete payload.recipient_email;
      const { data } = await api.post("/packages", payload);
      toast.success(`Package registered · ${data.tracking_id}`);
      setCreateOpen(false);
      setCreateForm(initialCreate);
      loadData();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const openCheckpoint = (pkg) => {
    setCpTarget(pkg);
    setCpForm(initialCheckpoint);
    setCpOpen(true);
  };

  const submitCheckpoint = async (e) => {
    e.preventDefault();
    const effectiveStatus =
      cpForm.status === CUSTOM_STATUS_SENTINEL ? (cpForm.custom_status || "").trim() : cpForm.status;
    if (!effectiveStatus) {
      toast.error("Please enter a status");
      return;
    }
    setCpSaving(true);
    try {
      const { data } = await api.post(
        `/packages/${cpTarget.tracking_id}/checkpoints`,
        {
          status: effectiveStatus,
          location: cpForm.location,
          notes: cpForm.notes,
          notify_recipient: cpForm.notify_recipient,
        }
      );
      toast.success(
        `Status updated${data.email_sent ? " · notification email sent" : ""}`
      );
      setCpOpen(false);
      loadData();
      if (detailOpen && detailPkg?.tracking_id === cpTarget.tracking_id) {
        setDetailPkg(data.package);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setCpSaving(false);
    }
  };

  const openDetail = (pkg) => { setDetailPkg(pkg); setDetailOpen(true); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/packages/${encodeURIComponent(deleteTarget.tracking_id)}`);
      setPackages((prev) => prev.filter((p) => p.tracking_id !== deleteTarget.tracking_id));
      toast.success(`Shipment ${deleteTarget.tracking_id} deleted permanently`);
      setDeleteTarget(null);
      // Refresh KPIs so counts stay in sync
      try {
        const { data } = await api.get("/packages/stats/kpi");
        setKpi(data);
      } catch (err) {
        // Non-fatal — delete already succeeded; stale KPI will refresh on
        // the next full page load / manual refresh click.
        if (process.env.NODE_ENV !== "production") {
          console.warn("KPI refresh after delete failed:", err);
        }
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    if (pwdForm.new_password === pwdForm.current_password) {
      toast.error("New password must be different from the current one");
      return;
    }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success("Password updated · you remain signed in on this device");
      setPwdOpen(false);
      setPwdForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setPwdSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/contact/admin", { replace: true });
  };

  const kpiCards = [
    { label: "Total consignments", value: kpi.total, Icon: Package, color: "text-amber-400" },
    { label: "In transit", value: kpi.in_transit, Icon: Truck, color: "text-amber-300" },
    { label: "Vaulted", value: kpi.vaulted, Icon: Vault, color: "text-emerald-300" },
    { label: "Delivered", value: kpi.delivered, Icon: CheckCircle2, color: "text-emerald-400" },
  ];

  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 dvl-grain">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A1128]/85 border-b border-amber-500/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/contact/admin/dashboard" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md border border-amber-500/40 flex items-center justify-center bg-[#0F1C3F]">
              <Shield className="w-4 h-4 text-amber-400" />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-base">Command Deck</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 font-mono">
                Diplomatic Vaults & Logistics
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 hidden sm:inline">{user?.email}</span>
            <Link to="/" className="text-xs text-amber-300 hover:text-amber-200 font-mono uppercase tracking-widest">
              View site <ExternalLink className="w-3 h-3 inline ml-1" />
            </Link>
            <Button
              onClick={() => setPwdOpen(true)}
              variant="ghost"
              data-testid="change-password-button"
              className="text-slate-300 hover:text-amber-300 hover:bg-amber-500/10"
              title="Change password"
            >
              <KeyRound className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Password</span>
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              data-testid="admin-logout"
              className="text-slate-300 hover:text-amber-300 hover:bg-amber-500/10"
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400/80 mb-2">
              Operations
            </div>
            <h1 className="font-serif text-3xl md:text-4xl">Consignment control</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadData} variant="ghost" data-testid="refresh-button"
              className="text-slate-300 hover:text-amber-300 hover:bg-amber-500/10">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-package-button"
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold">
                  <Plus className="w-4 h-4 mr-1" /> Register package
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0F1C3F] border-amber-500/25 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Register new consignment</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitCreate} className="space-y-4" data-testid="create-package-form">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Sender name *</label>
                      <Input required data-testid="create-sender-name" value={createForm.sender_name}
                        onChange={(e) => setCreateForm({ ...createForm, sender_name: e.target.value })}
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Sender org</label>
                      <Input value={createForm.sender_org}
                        onChange={(e) => setCreateForm({ ...createForm, sender_org: e.target.value })}
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Recipient name *</label>
                      <Input required data-testid="create-recipient-name" value={createForm.recipient_name}
                        onChange={(e) => setCreateForm({ ...createForm, recipient_name: e.target.value })}
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Recipient org</label>
                      <Input value={createForm.recipient_org}
                        onChange={(e) => setCreateForm({ ...createForm, recipient_org: e.target.value })}
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Recipient email (for notifications)</label>
                      <Input type="email" value={createForm.recipient_email}
                        data-testid="create-recipient-email"
                        onChange={(e) => setCreateForm({ ...createForm, recipient_email: e.target.value })}
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Origin *</label>
                      <Input required data-testid="create-origin" value={createForm.origin}
                        onChange={(e) => setCreateForm({ ...createForm, origin: e.target.value })}
                        placeholder="e.g. London, UK"
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Destination *</label>
                      <Input required data-testid="create-destination" value={createForm.destination}
                        onChange={(e) => setCreateForm({ ...createForm, destination: e.target.value })}
                        placeholder="e.g. Zurich, CH"
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Service *</label>
                      <Select value={createForm.service_type}
                        onValueChange={(v) => setCreateForm({ ...createForm, service_type: v })}>
                        <SelectTrigger data-testid="create-service" className="bg-slate-950/80 border-amber-500/30 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0F1C3F] text-slate-100 border-amber-500/30">
                          {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Weight (kg) *</label>
                      <Input required type="number" step="0.1" data-testid="create-weight"
                        value={createForm.weight_kg}
                        onChange={(e) => setCreateForm({ ...createForm, weight_kg: e.target.value })}
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Declared value</label>
                      <Input value={createForm.declared_value}
                        onChange={(e) => setCreateForm({ ...createForm, declared_value: e.target.value })}
                        placeholder="Confidential / £X"
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Est. delivery</label>
                      <Input type="date" value={createForm.estimated_delivery}
                        onChange={(e) => setCreateForm({ ...createForm, estimated_delivery: e.target.value })}
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    {createForm.service_type === "Deposit" && (
                      <>
                        <div>
                          <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">
                            Deposit date *
                          </label>
                          <Input
                            type="date"
                            required
                            data-testid="create-deposit-date"
                            value={createForm.deposit_date}
                            onChange={(e) => setCreateForm({ ...createForm, deposit_date: e.target.value })}
                            className="bg-slate-950/80 border-amber-500/30 mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">
                            Depositor name *
                          </label>
                          <Input
                            required
                            data-testid="create-depositor-name"
                            value={createForm.depositor_name}
                            onChange={(e) => setCreateForm({ ...createForm, depositor_name: e.target.value })}
                            placeholder="e.g. H.E. Ambassador Al-Rashid"
                            className="bg-slate-950/80 border-amber-500/30 mt-1"
                          />
                        </div>
                      </>
                    )}
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Initial location *</label>
                      <Input required data-testid="create-initial-location" value={createForm.initial_location}
                        onChange={(e) => setCreateForm({ ...createForm, initial_location: e.target.value })}
                        placeholder="e.g. London HQ Vault Bay 3"
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Officer notes</label>
                      <Textarea rows={2} value={createForm.notes}
                        onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                        className="bg-slate-950/80 border-amber-500/30 mt-1" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} className="text-slate-300">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={creating} data-testid="create-package-submit"
                      className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold">
                      {creating ? "Registering…" : "Seal & register"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((k) => (
            <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-[#14244D] border border-amber-500/20 p-5 rounded-xl flex items-center gap-4 shadow-lg">
              <div className={`w-11 h-11 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center ${k.color}`}>
                <k.Icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400/70">{k.label}</div>
                <div className="font-serif text-3xl text-slate-50 mt-1">{k.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Company details */}
        <form
          onSubmit={submitCompany}
          data-testid="company-details-form"
          className="bg-[#0F1C3F]/70 border border-amber-500/20 rounded-2xl overflow-hidden"
        >
          <div className="p-5 border-b border-amber-500/15 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400/70">
                  Public site
                </div>
                <div className="font-serif text-xl text-slate-50">Company details</div>
              </div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
              {companyDirty ? (
                <span className="text-amber-300">Unsaved changes</span>
              ) : company.updated_at ? (
                <>Updated {new Date(company.updated_at).toLocaleString("en-GB")}</>
              ) : (
                <>Live on footer &amp; contact page</>
              )}
            </div>
          </div>

          {!companyForm ? (
            <div className="p-8 text-center text-slate-500 text-sm">Loading company details…</div>
          ) : (
            <div className="p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80 flex items-center gap-2">
                    <Mail className="w-3 h-3" /> Secure email
                  </label>
                  <Input
                    type="email"
                    required
                    value={companyForm.secure_email}
                    onChange={(e) => patchCompany({ secure_email: e.target.value })}
                    data-testid="company-email"
                    className="bg-slate-950/80 border-amber-500/30 mt-1 h-11"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80 flex items-center gap-2">
                    <Phone className="w-3 h-3" /> Phone number
                  </label>
                  <Input
                    type="tel"
                    required
                    value={companyForm.phone}
                    onChange={(e) => patchCompany({ phone: e.target.value })}
                    data-testid="company-phone"
                    className="bg-slate-950/80 border-amber-500/30 mt-1 h-11"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80 flex items-center gap-2">
                  <MapPin className="w-3 h-3" /> Registered address
                </label>
                <Textarea
                  required
                  rows={3}
                  value={companyForm.address}
                  onChange={(e) => patchCompany({ address: e.target.value })}
                  data-testid="company-address"
                  placeholder={"Street\nCity, Country"}
                  className="bg-slate-950/80 border-amber-500/30 mt-1"
                />
                <div className="text-[10px] font-mono text-slate-500 mt-1">
                  Line breaks are preserved on the public site.
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                {companyDirty && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setCompanyForm({
                        secure_email: company.secure_email,
                        phone: company.phone,
                        address: company.address,
                      });
                      setCompanyDirty(false);
                    }}
                    className="text-slate-300"
                    data-testid="company-reset"
                  >
                    Discard
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={companySaving || !companyDirty}
                  data-testid="company-save"
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {companySaving ? "Saving…" : "Save & publish"}
                </Button>
              </div>
            </div>
          )}
        </form>

        {/* Search + table */}
        <div className="bg-[#0F1C3F]/70 border border-amber-500/20 rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-amber-500/15 flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400/70" />
              <Input placeholder="Search by tracking ID, recipient, city…"
                data-testid="admin-search"
                value={q} onChange={(e) => setQ(e.target.value)}
                className="bg-slate-950/80 border-amber-500/30 pl-9 h-10" />
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-400">
              {filtered.length} of {packages.length}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="packages-table">
              <thead className="bg-[#0A1128]/60 text-amber-400/80 font-mono uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left px-5 py-3">Tracking</th>
                  <th className="text-left px-5 py-3">Recipient</th>
                  <th className="text-left px-5 py-3">Route</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Location</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="text-center py-10 text-slate-500">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-slate-500">
                    No consignments yet. Register your first package to begin.
                  </td></tr>
                )}
                {filtered.map((p) => (
                  <tr key={p.tracking_id} className="border-t border-amber-500/10 hover:bg-amber-500/5 transition-colors"
                    data-testid={`row-${p.tracking_id}`}>
                    <td className="px-5 py-4">
                      <span className="font-mono font-bold text-amber-300 bg-amber-950/30 border border-amber-500/25 px-2 py-1 rounded text-xs">
                        {p.tracking_id}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-slate-100">{p.recipient_name}</div>
                      <div className="text-xs text-slate-500">{p.recipient_org}</div>
                    </td>
                    <td className="px-5 py-4 text-slate-300 text-xs">
                      {p.origin} <span className="text-amber-500/80">→</span> {p.destination}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`border font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${statusTone(p.current_status)}`}>
                        {p.current_status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-300 text-xs">{p.current_location}</td>
                    <td className="px-5 py-4 text-right space-x-1 whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openDetail(p)}
                        data-testid={`view-${p.tracking_id}`}
                        className="text-slate-300 hover:text-amber-300 hover:bg-amber-500/10">
                        <ScrollText className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={() => openCheckpoint(p)}
                        data-testid={`update-${p.tracking_id}`}
                        className="bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/30">
                        Update status
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(p)}
                        data-testid={`delete-${p.tracking_id}`}
                        title="Delete shipment"
                        aria-label={`Delete shipment ${p.tracking_id}`}
                        className="text-slate-400 hover:text-red-300 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Update status dialog */}
      <Dialog open={cpOpen} onOpenChange={setCpOpen}>
        <DialogContent className="bg-[#0F1C3F] border-amber-500/25 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Update status
              {cpTarget && <span className="ml-2 font-mono text-sm text-amber-300">{cpTarget.tracking_id}</span>}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCheckpoint} className="space-y-4" data-testid="checkpoint-form">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Status</label>
              <Select value={cpForm.status} onValueChange={(v) => setCpForm({ ...cpForm, status: v, custom_status: v === CUSTOM_STATUS_SENTINEL ? cpForm.custom_status : "" })}>
                <SelectTrigger data-testid="checkpoint-status" className="bg-slate-950/80 border-amber-500/30 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0F1C3F] text-slate-100 border-amber-500/30">
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  <SelectItem value={CUSTOM_STATUS_SENTINEL}>Custom status…</SelectItem>
                </SelectContent>
              </Select>
              {cpForm.status === CUSTOM_STATUS_SENTINEL && (
                <Input
                  required
                  autoFocus
                  data-testid="checkpoint-custom-status"
                  value={cpForm.custom_status}
                  onChange={(e) => setCpForm({ ...cpForm, custom_status: e.target.value })}
                  placeholder="e.g. Awaiting cryptographic verification"
                  maxLength={80}
                  className="bg-slate-950/80 border-amber-500/30 mt-2"
                />
              )}
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">New location *</label>
              <Input required data-testid="checkpoint-location" value={cpForm.location}
                onChange={(e) => setCpForm({ ...cpForm, location: e.target.value })}
                placeholder="e.g. Zurich Vault Hub — Terminal 4"
                className="bg-slate-950/80 border-amber-500/30 mt-1" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Officer notes</label>
              <Textarea rows={3} value={cpForm.notes}
                data-testid="checkpoint-notes"
                onChange={(e) => setCpForm({ ...cpForm, notes: e.target.value })}
                className="bg-slate-950/80 border-amber-500/30 mt-1" />
            </div>
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-amber-500/20 bg-slate-950/40">
              <Checkbox
                checked={cpForm.notify_recipient}
                onCheckedChange={(v) => setCpForm({ ...cpForm, notify_recipient: !!v })}
                data-testid="checkpoint-notify"
                className="border-amber-500/40 data-[state=checked]:bg-amber-500 data-[state=checked]:text-slate-950"
              />
              <span className="flex items-center gap-2 text-sm">
                <Bell className="w-4 h-4 text-amber-400" />
                Send email notification to recipient
              </span>
            </label>
            {cpTarget && !cpTarget.recipient_email && cpForm.notify_recipient && (
              <div className="text-xs text-amber-300/80 border border-amber-500/30 rounded px-3 py-2">
                No recipient email on file — notification will be skipped.
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCpOpen(false)} className="text-slate-300">
                Cancel
              </Button>
              <Button type="submit" disabled={cpSaving} data-testid="checkpoint-submit"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold">
                {cpSaving ? "Saving…" : "Add checkpoint"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-[#0F1C3F] border-amber-500/25 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Consignment record
              {detailPkg && <span className="ml-2 font-mono text-sm text-amber-300">{detailPkg.tracking_id}</span>}
            </DialogTitle>
          </DialogHeader>
          {detailPkg && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Sender</div>
                  <div>{detailPkg.sender_name}</div>
                  <div className="text-xs text-slate-500">{detailPkg.sender_org}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Recipient</div>
                  <div>{detailPkg.recipient_name}</div>
                  <div className="text-xs text-slate-500">{detailPkg.recipient_org}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Origin</div>
                  <div>{detailPkg.origin}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Destination</div>
                  <div>{detailPkg.destination}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Service</div>
                  <div>{detailPkg.service_type}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Weight</div>
                  <div>{detailPkg.weight_kg} kg</div></div>
                {detailPkg.deposit_date && (
                  <div><div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Deposit date</div>
                    <div>{new Date(detailPkg.deposit_date).toLocaleDateString("en-GB")}</div></div>
                )}
                {detailPkg.depositor_name && (
                  <div><div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">Depositor</div>
                    <div>{detailPkg.depositor_name}</div></div>
                )}
              </div>
              <ol className="relative border-l border-amber-500/25 ml-3 space-y-6 pt-2">
                {[...detailPkg.checkpoints].reverse().map((c, i) => (
                  <li key={`${c.timestamp}-${c.status}`} className="pl-6 relative">
                    <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 ${i === 0 ? "bg-amber-400 border-amber-300" : "bg-[#0A1128] border-amber-500/50"}`} />
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className={`border font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${statusTone(c.status)}`}>
                        {c.status}
                      </span>
                      <span className="text-xs text-slate-500 font-mono">
                        {new Date(c.timestamp).toLocaleString("en-GB")}
                      </span>
                    </div>
                    <div className="flex items-start gap-2 mt-1 text-slate-100">
                      <MapPin className="w-4 h-4 text-amber-400 mt-1 shrink-0" />
                      <div>
                        <div className="font-medium">{c.location}</div>
                        {c.notes && <div className="text-sm text-slate-400 mt-0.5">{c.notes}</div>}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="bg-[#0F1C3F] border-amber-500/25 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              <KeyRound className="w-5 h-5 text-amber-400" /> Change password
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Rotate your admin password. You&rsquo;ll stay signed in on this device.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={submitPasswordChange}
            className="space-y-4"
            data-testid="change-password-form"
          >
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">
                Current password
              </label>
              <Input
                type="password"
                required
                autoComplete="current-password"
                value={pwdForm.current_password}
                onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                data-testid="pwd-current"
                className="bg-slate-950/80 border-amber-500/30 mt-1 h-11"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">
                New password
              </label>
              <Input
                type="password"
                required
                autoComplete="new-password"
                value={pwdForm.new_password}
                onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                data-testid="pwd-new"
                className="bg-slate-950/80 border-amber-500/30 mt-1 h-11"
              />
              <div className="text-[10px] text-slate-500 mt-1 font-mono">
                Minimum 8 characters. Mix upper/lower/number for a strong seal.
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">
                Confirm new password
              </label>
              <Input
                type="password"
                required
                autoComplete="new-password"
                value={pwdForm.confirm_password}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })}
                data-testid="pwd-confirm"
                className="bg-slate-950/80 border-amber-500/30 mt-1 h-11"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPwdOpen(false)}
                className="text-slate-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={pwdSaving}
                data-testid="pwd-submit"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold"
              >
                {pwdSaving ? "Sealing…" : "Update password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete shipment confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent
          className="bg-[#0F1C3F] border-red-500/40 text-slate-100 max-w-md"
          data-testid="delete-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" /> Delete shipment?
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              This permanently removes the record from the database and from
              all tracking results. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="bg-slate-950/60 border border-red-500/30 rounded-lg p-4 my-2 space-y-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-red-300/80">
                Tracking ID
              </div>
              <div className="font-mono font-bold text-amber-300">
                {deleteTarget.tracking_id}
              </div>
              <div className="text-xs text-slate-400 mt-2">
                {deleteTarget.recipient_name}
                {deleteTarget.recipient_org ? ` · ${deleteTarget.recipient_org}` : ""}
              </div>
              <div className="text-xs text-slate-500">
                {deleteTarget.origin} → {deleteTarget.destination}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              className="text-slate-300"
              data-testid="delete-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="delete-confirm"
              className="bg-red-600 hover:bg-red-500 text-white font-semibold"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
